import { ApplicationElement } from '@effectivastudio/lit-components';
import Trix from 'trix/dist/trix.esm.js';
import debounce from 'lodash-es/debounce.js';
import extractHashtagsWithIndices from 'twitter-text/dist/esm/extractHashtagsWithIndices.js';
import isValidHashtag from 'twitter-text/dist/esm/isValidHashtag.js';
import { Database as EmojiDB } from 'emoji-picker-element';

// Trix config
Trix.config.blockAttributes.heading1.tagName = 'h3';
Trix.config.textAttributes.span = { tagName: 'span' };
Trix.config.textAttributes.hashtag = { tagName: 'hashtag' };

const translationKeys = [
  'bold',
  'bullets',
  'byte',
  'bytes',
  'captionPlaceholder',
  'code',
  'heading1',
  'indent',
  'italic',
  'link',
  'numbers',
  'outdent',
  'quote',
  'redo',
  'remove',
  'strike',
  'undo',
  'unlink',
  'url',
  'urlPlaceholder',
  'GB',
  'KB',
  'MB',
  'PB',
  'TB',
];
const EditorController = Trix.controllers.EditorController;
const { inputControllerDidReceiveKeyboardCommand } = EditorController.prototype;

export function setTrixTranslations() {
  translationKeys.forEach(key => {
    Trix.config.lang[key] = ApplicationElement.i18n.t(`trix.${key}`);
  });
}

// Disable CTRL+K shortcut for inserting links in mini layout (chat)
function inputControllerDidReceiveKeyboardCommandOverride() {
  const isMini = this.editorElement.closest('bu-rte').layout === 'mini';
  const [keys] = arguments;
  if (isMini && keys[0] === 'k') return true;

  return inputControllerDidReceiveKeyboardCommand.apply(this, arguments);
}
EditorController.prototype.inputControllerDidReceiveKeyboardCommand =
  inputControllerDidReceiveKeyboardCommandOverride;

export const addLeadingSpace = (editor, insertPosition = 'after') => {
  const position =
    insertPosition === 'before'
      ? editor.getPosition() - 1
      : editor.getPosition();
  const text = editor.getDocument().toString();
  const character = text.charAt(position);
  character && character !== ' ' && editor.insertString(' ');
};

export const debouncedRemoteSearch = url => {
  return debounce(async (text, cb) => {
    try {
      const response = await fetch(`${url}?query=${text}`);
      const data = await response.json();
      data.forEach(item => {
        item.key = `${item.first_name} ${item.last_name}`;
      });
      cb(data);
    } catch (error) {
      cb([]);
    }
  }, 200);
};

const emojiDB = new EmojiDB();

export async function searchEmojiDB(query, cb) {
  const result = await emojiDB.getEmojiBySearchQuery(query);
  cb(result.map(e => ({ key: e.annotation, value: e.unicode })));
}

export function tributeMentionTemplate(item) {
  const { key: name, avatar_url } = item.original;

  return `
    <span class="mention-item">
      <ef-avatar
        ${avatar_url ? `picture=${avatar_url}` : ''}
        name='${name}'
        rounded
        layout='xs'>
      </ef-avatar>
      <div class="title">${item.string}</div>
    </span>
  `;
}

export function tributeEmojiTemplate(item) {
  return `<span>${item.original.value} <small>${item.string}</small></span>`;
}

export const hashtagParser = editor => {
  if (!editor) {
    return;
  }

  const position = editor.getSelectedRange();

  // Return if selection range is present
  if (position[0] !== position[1]) {
    return;
  }

  let hashtags = editor.getDocument().findRangesForTextAttribute('hashtag');

  // Remove Hashtag tag if hashtag is not valid
  hashtags.forEach((hashtagRange, i) => {
    editor.setSelectedRange(hashtagRange);
    const selectedString = editor.getSelectedDocument().toString().trim();

    !isValidHashtag(selectedString) && editor.deactivateAttribute('hashtag');
    i === hashtags.length - 1 && editor.setSelectedRange(position);
  });

  // Set Hashtag tag if hashtag is valid
  const str = editor.getDocument().toString();
  hashtags = extractHashtagsWithIndices(str);

  hashtags.forEach((hashtag, i) => {
    editor.setSelectedRange([hashtag.indices[0], hashtag.indices[1]]);

    !editor.attributeIsActive('hashtag') && editor.activateAttribute('hashtag');
    i === hashtags.length - 1 && editor.setSelectedRange(position);
  });
};

export const debouncedHashtagParser = debounce(editor => {
  hashtagParser(editor);
}, 400);
